import React, { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF, useAnimations, ContactShadows } from "@react-three/drei";

class GLBErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    return this.state.err ? this.props.fallback : this.props.children;
  }
}

function GLBModel({ url, scale, offsetY, still }) {
  const group = useRef();
  const { scene, animations } = useGLTF(url);
  const { actions, names } = useAnimations(animations, group);

  // Molti .glb umanoidi includono una clip idle/breathing incorporata: se non la
  // suoniamo affatto, il rig resta nella bind pose (braccia larghe, il classico
  // "T-pose") — per questo "still" non ferma l'azione (stop()), la mette in pausa
  // al primo fotogramma: la posa resta quella naturale della clip, semplicemente
  // congelata, finché l'ospite non interagisce (Inizia/microfono).
  useEffect(() => {
    if (!names.length) return;
    const action = actions[names[0]];
    if (!action) return;
    if (still) {
      action.reset().play();
      action.paused = true;
      return () => {
        action.paused = false;
      };
    }
    action.reset().fadeIn(0.4).play();
    return () => action.fadeOut(0.4);
  }, [names, actions, still]);

  useFrame((state) => {
    if (group.current && !still) {
      group.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.15;
    }
  });

  return <primitive ref={group} object={scene} scale={scale} position={[0, offsetY, 0]} />;
}

function ProceduralAvatar({ speaking, still }) {
  const group = useRef();
  const mouth = useRef();
  const lidL = useRef();
  const lidR = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (group.current && !still) {
      group.current.position.y = Math.sin(t * 1.2) * 0.05;
      group.current.rotation.y = Math.sin(t * 0.4) * 0.22;
    }
    if (mouth.current) {
      const open = speaking ? 0.12 + Math.abs(Math.sin(t * 12)) * 0.55 : 0.06;
      mouth.current.scale.y = open;
    }
    // blink
    const blink = Math.sin(t * 2.3) > 0.98 ? 0.05 : 1;
    if (lidL.current) lidL.current.scale.y = blink;
    if (lidR.current) lidR.current.scale.y = blink;
  });
  return (
    <group ref={group}>
      <mesh position={[0, 0.2, 0]}>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial color="#2f6bff" roughness={0.35} metalness={0.15} />
      </mesh>
      <mesh position={[0, 0.15, 0.72]}>
        <sphereGeometry args={[0.62, 48, 48]} />
        <meshStandardMaterial color="#eef3ff" roughness={0.5} />
      </mesh>
      <mesh ref={lidL} position={[-0.24, 0.3, 1.12]}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh ref={lidR} position={[0.24, 0.3, 1.12]}>
        <sphereGeometry args={[0.1, 32, 32]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
      <mesh ref={mouth} position={[0, -0.08, 1.16]}>
        <capsuleGeometry args={[0.16, 0.18, 8, 16]} />
        <meshStandardMaterial color="#0a0a0a" />
      </mesh>
    </group>
  );
}

export default function AvatarCanvas({
  modelUrl, provider, speaking, backgroundUrl, modelScale = 1.2, modelOffsetY = -1.4, className, still = false,
}) {
  const useGlb = provider === "readyplayerme" && modelUrl;
  return (
    <div
      className={`bg-cover bg-center ${className || ""}`}
      style={backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
      data-testid="avatar-canvas-wrap"
    >
      <Canvas camera={{ position: [0, 0.3, 4], fov: 42 }} dpr={[1, 2]}>
        <ambientLight intensity={0.75} />
        <directionalLight position={[3, 5, 4]} intensity={1.15} />
        <directionalLight position={[-4, 2, -2]} intensity={0.5} color="#7aa2ff" />
        <Suspense fallback={null}>
          {useGlb ? (
            <GLBErrorBoundary fallback={<ProceduralAvatar speaking={speaking} still={still} />}>
              <GLBModel url={modelUrl} scale={modelScale} offsetY={modelOffsetY} still={still} />
            </GLBErrorBoundary>
          ) : (
            <ProceduralAvatar speaking={speaking} still={still} />
          )}
        </Suspense>
        <ContactShadows position={[0, -1.5, 0]} opacity={0.35} scale={9} blur={2.5} far={4} />
      </Canvas>
    </div>
  );
}
